import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  SettingsFeature,
  SettingsAgentConfig,
  ModelTag,
  SettingsDeveloper,
  AppMode,
  ChatMode,
  SettingsActor,
  SettingsAnalytics,
} from '../../types';
import { GENERIC_APP } from '../../config/generic';
import { SOS_APP } from '../../config/sos';
import { BASE_APP } from '../../config/base';

const KEY_SETTINGS_DEV = 'settings_developer';
const KEY_SETTINGS_ANALYTICS = 'settings_analytics';

function loadSetting(key: string) {
  let value = localStorage.getItem(key);

  if (
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' &&
    key === 'actor' &&
    value != null
  ) {
    const actorObj = JSON.parse(value);
    actorObj.modelTag = 'claude-4.5-sonnet';
    saveSetting(key, actorObj);
    value = localStorage.getItem(key);
  }

  if (
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' &&
    key === 'assistant' &&
    value != null
  ) {
    const assistantObj = JSON.parse(value);
    assistantObj.modelTag = 'gpt-5.2';
    assistantObj.proposeResponseModelTag = 'gpt-5.2';
    saveSetting(key, assistantObj);
    value = localStorage.getItem(key);
  }

  try {
    return JSON.parse(value || '{}');
  } catch {
    return {};
  }
}

function saveSetting(key: string, obj: object) {
  localStorage.setItem(key, JSON.stringify(obj));
}

function loadSettingsFeature(key: string) {
  return loadSetting(key);
}

function saveSettingsFeature(feature: SettingsFeature) {
  const { isAutoRun, isEnabled } = feature;
  saveSetting(feature.key, { isAutoRun, isEnabled });
}

function loadAgentConfig(key: string) {
  return loadSetting(key);
}

function saveAgentConfig(config: SettingsAgentConfig) {
  const { key, ...others } = config;
  saveSetting(key, others);
}

interface SettingsState {
  appMode: AppMode;
  analytics: SettingsAnalytics;
  dev: SettingsDeveloper;
  features: SettingsFeature[];
  actorConfig: SettingsActor;
  assistantConfig: SettingsAgentConfig;
  chatMode: ChatMode;
}

const featureSettings = BASE_APP.featureSettings.map((feat) => ({
  ...feat,
  ...loadSettingsFeature(feat.key),
}));

const initialState: SettingsState = {
  appMode: 'dialogue',
  analytics: {
    showTrafficLight: false,
    autoTrafficLight: false,
    reasoning: process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS',
    autoEvaluateMessageRisk: false,
    autoEvaluateSentiment: false,
    evaluateSuicidality: false,
    ...loadSetting(KEY_SETTINGS_ANALYTICS),
  },
  dev: {
    maskClientMessage: false,
    showMessageModelTag: false,
    showSelfPlayOptions: false,
    includeExplanationState: false,
    ...loadSetting(KEY_SETTINGS_DEV),
  },
  features:
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? featureSettings.concat(
          GENERIC_APP.featureSettings.map((feat) => ({
            ...feat,
            ...loadSettingsFeature(feat.key),
          }))
        )
      : featureSettings.concat(
          SOS_APP.featureSettings.map((feat) => ({
            ...feat,
            ...loadSettingsFeature(feat.key),
          }))
        ),
  actorConfig: {
    key: 'actor',
    label: 'Actor',
    modelTag: 'claude-4.5-sonnet',
    actorProfile: true,
    automaticReplies: true,
    ...loadAgentConfig('actor'),
  },
  assistantConfig: {
    key: 'assistant',
    label: 'Assistant',
    modelTag: 'gpt-5.2',
    proposeResponseModelTag: 'gpt-5.2',
    ...loadAgentConfig('assistant'),
  },
  chatMode: 'clients',
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setAppMode: (state, action: PayloadAction<AppMode>) => {
      state.appMode = action.payload;
    },
    setChatMode: (state, action: PayloadAction<ChatMode>) => {
      state.chatMode = action.payload;
    },
    setSettingsAnalytics: (state, action: PayloadAction<SettingsAnalytics>) => {
      saveSetting(KEY_SETTINGS_ANALYTICS, action.payload);
      state.analytics = action.payload;
    },
    setSettingsDeveloper: (state, action: PayloadAction<SettingsDeveloper>) => {
      saveSetting(KEY_SETTINGS_DEV, action.payload);
      state.dev = action.payload;
    },
    setSettingsFeature: (state, action: PayloadAction<SettingsFeature>) => {
      saveSettingsFeature(action.payload);
      state.features = state.features.map((feat) =>
        feat.key === action.payload.key ? action.payload : feat
      );
    },
    setActorModel: (state, action: PayloadAction<ModelTag>) => {
      state.actorConfig.modelTag = action.payload;
      saveAgentConfig(state.actorConfig);
    },
    setTypingSimulated: (state, action: PayloadAction<boolean>) => {
      state.actorConfig.typingSimulated = action.payload;
      saveAgentConfig(state.actorConfig);
    },
    setAutomaticReplies: (state, action: PayloadAction<boolean>) => {
      state.actorConfig.automaticReplies = action.payload;
      saveAgentConfig(state.actorConfig);
    },
    setActorDetails: (state, action: PayloadAction<boolean>) => {
      state.actorConfig.actorDetails = action.payload;
      saveAgentConfig(state.actorConfig);
    },
    setActorProfile: (state, action: PayloadAction<boolean>) => {
      state.actorConfig.actorProfile = action.payload;
      saveAgentConfig(state.actorConfig);
    },
    setAssistantModel: (state, action: PayloadAction<ModelTag>) => {
      state.assistantConfig.modelTag = action.payload;
      saveAgentConfig(state.assistantConfig);
    },
    setProposeResponseModel: (state, action: PayloadAction<ModelTag>) => {
      state.assistantConfig.proposeResponseModelTag = action.payload;
      saveAgentConfig(state.assistantConfig);
    },
  },
});

export const {
  setAppMode,
  setChatMode,
  setSettingsAnalytics,
  setSettingsDeveloper,
  setSettingsFeature,
  setActorModel,
  setTypingSimulated,
  setAutomaticReplies,
  setActorDetails,
  setActorProfile,
  setAssistantModel,
  setProposeResponseModel,
} = settingsSlice.actions;

export const selectAppMode = (state: RootState) => state.settings.appMode;

export const selectChatMode = (state: RootState) => state.settings.chatMode;

export const selectSettingsAnalytics = (state: RootState) =>
  state.settings.analytics;

export const selectSettingsDeveloper = (state: RootState) => state.settings.dev;

export const selectSettingsFeatures = (state: RootState) =>
  state.settings.features;

export const selectActorConfig = (state: RootState) =>
  state.settings.actorConfig;

export const selectAssistantConfig = (state: RootState) =>
  state.settings.assistantConfig;

export default settingsSlice.reducer;
